import { useEffect, useState } from 'react';

interface SharePanelProps {
  open: boolean;
  onClose: () => void;
  siteName: string;
  shareUrl: string;
}

export default function SharePanel({ open, onClose, siteName, shareUrl }: SharePanelProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/50" onClick={onClose} aria-hidden="true"></div>
      <div className="relative w-full max-w-md rounded-lg bg-background-50 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950">分享该网站</h3>
            <p className="mt-1 text-xs text-foreground-500">把「{siteName}」推荐给朋友</p>
          </div>
          <button
            type="button"
            aria-label="关闭分享面板"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="mt-5 flex flex-col items-center rounded-lg border border-background-200 bg-background-100 p-5">
          <div className="h-[180px] w-[180px] overflow-hidden rounded-md border border-background-200 bg-background-50 p-2">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(shareUrl)}`}
              alt={`${siteName} 分享二维码`}
              title={`扫码访问 ${siteName}`}
              className="h-full w-full object-contain"
            />
          </div>
          <p className="mt-3 flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-wechat-line text-accent-600"></i>
            微信扫一扫，打开该网站
          </p>
        </div>

        <div className="mt-4 flex items-center gap-2 rounded-md border border-background-300 bg-background-50 p-2 pl-3">
          <span className="min-w-0 flex-1 truncate text-xs text-foreground-600">{shareUrl}</span>
          <button
            type="button"
            onClick={handleCopy}
            className="flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
            {copied ? '已复制' : '复制网址'}
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {[
            { icon: 'ri-wechat-line', label: '微信' },
            { icon: 'ri-weibo-line', label: '微博' },
            { icon: 'ri-qq-line', label: 'QQ' },
            { icon: 'ri-links-line', label: '链接' },
          ].map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={handleCopy}
              className="flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className={`${item.icon} text-base`}></i>
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}